import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { css } from '@emotion/core';
import { useAlert } from '@weave/alert-system';
import {
  ConfirmationModal,
  IconButton,
  LocationIcon,
  PrimaryButton,
  Text,
  TextButton,
  TextField,
  UpdateIcon,
  DeleteIcon,
  useFormField,
  useModalControl,
  XIcon,
  KeyNames,
} from '@weave/design-system';
import { WeaveTheme } from '@weave/theme-original';

import { TerminalButton } from './terminal/terminal-button.component';
import { ViewLocationsPanel } from './terminal/view-locations-panel.component';
import {
  terminalTabCommandsContainer,
  terminalButtonsSection,
  terminalTabText,
  terminalActionsSection,
  xIconStyle,
  mBottomSpacing,
  alignCenter,
} from '../sync-app-card.styles';
import {
  GetSyncAppInfoPayload,
  TerminalTabProps,
  ConfirmModalType,
  SyncAppButtonInterface,
} from '../../sync-app.types';
import { copyStyles } from '../../../../helpers/utils';
import { getTerminalButtons } from '../sync-app-card.utils';
import { SyncAppSettingsQueries } from '../../../../apis/sync-app-locations/queries';

export const TerminalTab = (props: TerminalTabProps) => {
  const {
    sourceId,
    loading,
    socketOpen,
    terminalPermissions,
    terminalMessages = [],
    deleteSourceFunc,
    getSyncAppInfo,
    addToTerminalText,
    clearTerminalText,
    externalWindowDoc,
  } = props;
  const [confirmModalType, setConfirmModalType] = useState<ConfirmModalType | null>(null);
  const [terminalButtons, setTerminalButtons] = useState<SyncAppButtonInterface[]>([]);
  const [isLocationsPanelOpen, setIsLocationsPanelOpen] = useState(false);

  const fieldProps = useFormField({ type: 'text' });
  const alerts = useAlert();
  const { modalProps, triggerProps } = useModalControl();

  const {
    data: whitelistedLocations,
    isFetching: isFetchingWhitelistedLocations,
    refetch: refetchWhitelistedLocations,
  } = SyncAppSettingsQueries.useWhitelistedLocationsQuery(sourceId, {
    onError: (error) => {
      setIsLocationsPanelOpen(false);
      alerts.error(error?.message ?? 'Failed to load whitelisted locations');
    },
  });

  useEffect(() => {
    if (!props.connectionId) {
      props.getConnectionId();
    }
    const buttons = getTerminalButtons(terminalPermissions);
    setTerminalButtons(buttons);
    if (externalWindowDoc) {
      externalWindowDoc.head.innerHTML = '';
      copyStyles(document, externalWindowDoc);
    }
  }, []);

  const sendRequest = (rpcMethod: string, rpcParams: any = [], id?: string) => {
    const params: GetSyncAppInfoPayload = {
      sourceId,
      rpcMethod,
      rpcParams,
    };
    if (id) {
      params.id = id;
    }
    if (Array.isArray(rpcParams) && rpcParams.length > 0) {
      params.rpcParams = btoa(JSON.stringify(rpcParams[0]));
    }
    if (rpcMethod === 'RPCAdapter.SyncPatients') {
      addToTerminalText({ sourceId, terminalText: 'Patient sync started, please wait' });
    }
    getSyncAppInfo(params);
  };

  const closeModal = () => {
    modalProps.onClose();
    setConfirmModalType(null);
  };

  const showModal = (type: ConfirmModalType) => {
    setConfirmModalType(type);
    triggerProps.onClick();
  };

  const handleModalConfirmClick = () => {
    addToTerminalText({
      sourceId,
      terminalText: `${confirmModalType} action started ...`,
    });
    try {
      if (confirmModalType === ConfirmModalType.Purge) {
        sendRequest('RPCAdapter.Purge');
      } else if (confirmModalType === ConfirmModalType.PauseSync) {
        sendRequest('RPCAdapter.PauseSync', [], '1');
      } else if (confirmModalType === ConfirmModalType.ResumeSync) {
        sendRequest('RPCAdapter.ResumeSync', [], '1');
      } else if (confirmModalType === ConfirmModalType.Terminate) {
        const slug = props.slug.toUpperCase();
        sendRequest('OS.TerminateAdapter', [slug], '1');
      } else if (confirmModalType === ConfirmModalType.Restart) {
        sendRequest('RPCAdapter.Restart', [], '1');
      } else if (confirmModalType === ConfirmModalType.DeleteSource) {
        deleteSourceFunc({ sourceId });
      }
    } catch (e) {
      alerts.error(`${confirmModalType} action failed to start`);
    }
  };

  const getModalMessage = () => {
    if (confirmModalType === ConfirmModalType.PauseSync) {
      return 'Are you sure you want to pause data syncing?';
    }
    if (confirmModalType === ConfirmModalType.ResumeSync) {
      return 'Are you sure you want to resume data syncing?';
    }
    return `Are you sure you want to ${
      confirmModalType ? confirmModalType.toLowerCase().replace(' sync', '') : ''
    } this sync app?`;
  };

  const handleClearTerminalText = () => {
    clearTerminalText(sourceId);
  };

  const handleEnterKeyPress = (e) => {
    if (e.key === KeyNames.Enter) {
      try {
        const parsedMessage = JSON.parse(fieldProps.value);
        const rpcMethod = parsedMessage.method || parsedMessage.rpcMethod;
        const rpcParams = parsedMessage.params || parsedMessage.rpcParams || [];
        if (rpcMethod) {
          sendRequest(rpcMethod, rpcParams, parsedMessage.id);
        } else {
          addToTerminalText({ sourceId, terminalText: 'Invalid command' });
        }
      } catch {
        addToTerminalText({ sourceId, terminalText: 'Invalid command' });
      }
    }
  };

  const containerRef = useRef<HTMLInputElement>(null);
  const autoScrollEnabled = useRef(true);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (container && autoScrollEnabled.current) {
      container.scrollTo({
        top: container?.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [terminalMessages]);

  // This handler detects user scrolling
  const handleScroll = () => {
    const container = containerRef.current;
    if (container) {
      const { scrollHeight, scrollTop, clientHeight } = container;
      // A small tolerance (e.g., 5 pixels) helps account for rendering nuances.
      const isAtBottom = scrollHeight - scrollTop <= clientHeight + 5;

      // If the user scrolls up, disable auto-scroll.
      // If they scroll back to the bottom, re-enable it.
      autoScrollEnabled.current = isAtBottom;
    }
  };

  const handleViewLocationsClick = () => {
    setIsLocationsPanelOpen(true);
    refetchWhitelistedLocations();
  };

  if (socketOpen) {
    return (
      <>
        <div css={terminalTabCommandsContainer}>
          <div css={terminalActionsSection}>
            <div css={terminalButtonsSection}>
              {terminalButtons.map((item, index) => (
                <TerminalButton
                  key={index}
                  loading={loading}
                  sendRequest={sendRequest}
                  {...item}
                  {...(item.confirmModalType && { showModal })}
                />
              ))}
              <PrimaryButton
                onClick={handleViewLocationsClick}
                disabled={loading}
                css={viewLocationsButtonStyle}
              >
                <LocationIcon css={viewLocationsIconStyle} />
                View Locations
              </PrimaryButton>
            </div>
            <IconButton
              css={xIconStyle}
              label="Clear terminal"
              hoverLabelPlacement="left"
              disabled={terminalMessages.length === 0}
              showLabelOnHover
            >
              <XIcon onClick={handleClearTerminalText} />
            </IconButton>
          </div>
          <ViewLocationsPanel
            isOpen={isLocationsPanelOpen}
            isLoading={isFetchingWhitelistedLocations}
            data={whitelistedLocations}
          />
          {terminalPermissions.manageAdvancedPermission && (
            <>
              <TextField
                name="custom_cmd"
                label="Send Command"
                {...fieldProps}
                onKeyUp={handleEnterKeyPress}
                clearable
              />
              <Text as="span" color="light" css={mBottomSpacing(2)}>
                Press enter to send command
              </Text>
            </>
          )}
        </div>

        <div ref={containerRef} onScroll={handleScroll} css={terminalTabText}>
          {terminalMessages.map((message, id) => (
            <Text key={id} color="light">
              {message}
            </Text>
          ))}
        </div>

        <ConfirmationModal
          show={modalProps.show}
          onClose={closeModal}
          onConfirm={handleModalConfirmClick}
          confirmLabel={confirmModalType || 'Confirm'}
          title="Confirm this action"
          message={getModalMessage()}
        />
      </>
    );
  } else {
    return (
      <div css={[terminalTabCommandsContainer, alignCenter]}>
        <TextButton
          onClick={props.getConnectionId}
          icon={UpdateIcon}
          css={mBottomSpacing(2)}
        >
          Retry
        </TextButton>
        {terminalPermissions.installAdvancedPermission && (
          <TextButton
            onClick={() => {
              showModal(ConfirmModalType.DeleteSource);
            }}
            icon={DeleteIcon}
            css={mBottomSpacing(2)}
          >
            Delete Source
          </TextButton>
        )}
        <ConfirmationModal
          show={modalProps.show}
          onClose={closeModal}
          onConfirm={handleModalConfirmClick}
          confirmLabel={confirmModalType || 'Confirm'}
          title="Confirm this action"
          message={getModalMessage()}
        />
      </div>
    );
  }
};

const viewLocationsButtonStyle = (theme: WeaveTheme) => css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing(1)};
`;

const viewLocationsIconStyle = (theme: WeaveTheme) => css`
  width: ${theme.spacing(2)};
  height: ${theme.spacing(2)};
`;
